import React, { useEffect, useState } from 'react';
import logo from '../assets/nl_logo.svg';
import background from '../assets/forms_illustration.svg';
import PasswordField from '../components/PasswordField/PasswordField';
import {
  decodeToken,
  fetchSetupProfile,
  getEnv,
  getSetUpProfileErrorMessageID,
  validatePassword,
} from '../utils/utils';
import { isMobile } from 'react-device-detect';
import { ProfileFieldProps } from '../components/types';
import { SetupStepper } from '../components/SetupStepper/SetupStepper';
import { ProfileField } from '../components/ProfileField/ProfileField';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { BrickAnimation } from '../components/BrickAnimation/BrickAnimation';
import { Amplitude } from '../amplitude/amplitude';
import * as Sentry from '@sentry/react';

// In development, useEffect will fire twice due to StrictMode in React.
// This will cause bug in the backend hence we must prevent the second call.
// https://react.dev/learn/you-might-not-need-an-effect#initializing-the-application
let initDone = false;

/**
 * Displays the logo, title, stepper, and background image. Stores profile state
 * and passes it to the password and profile fields.
 */
export const SetupProfile = ({
  isCreatingWorkspace,
}: {
  isCreatingWorkspace: boolean;
}) => {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const steps = [
    intl.formatMessage({ id: 'password' }),
    intl.formatMessage({ id: 'profile' }),
  ];
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [profile, setProfile] = useState<ProfileFieldProps['profile']>({
    token: '',
    email: '',
    sso: false,
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    country: '',
    termsOfUse: false,
    salesMarketingConsent: false,
  });

  // The handler for the next button. If the password is valid, go to the next step.
  const handleNext = () => {
    const { id, databaseID } = decodeToken(profile.token);
    Amplitude.getInstance(id).track('setupProfileNext', {
      email: profile.email,
      country: profile.country,
      databaseID,
      userType: isCreatingWorkspace ? 'newUser' : 'invitedUser',
    });
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  // The handler for the stepper. If the user is in the first step (password) and
  // the password is valid, enable the second step (profile). If the user is in
  // the second step, the user can click on the first step to go back.
  const handleStep = (step: number) => {
    if (validatePassword(profile.password, profile.confirmPassword))
      setActiveStep(step);
  };

  // Gets the email and country from the token. If the user has given permission
  // to access the device location, change the country from the token to the
  // country from the device location.
  const fetchEmailAndCountry = async (
    isCreatingWorkspace: boolean,
  ): Promise<void> => {
    const env = getEnv();

    try {
      const setupProfile = await fetchSetupProfile(isCreatingWorkspace);
      const errorCode = setupProfile.errorCode;
      if (errorCode) {
        const errorMessage = getSetUpProfileErrorMessageID(
          errorCode,
          location.pathname,
        );
        navigate('/error', {
          replace: true,
          state: {
            profile: setupProfile,
            env,
            errorMessage,
          },
        });
      } else if (setupProfile.hasExistingProfile) {
        const successMessageTitleID = location.pathname.includes(
          'sign-up-final',
        )
          ? 'success.title.email_verified'
          : 'success.title.invite_accepted';
        navigate('/success', {
          replace: true,
          state: {
            profile: setupProfile,
            env,
            apiUrl: setupProfile.apiUrl,
            title: intl.formatMessage({ id: successMessageTitleID }),
          },
        });
      } else {
        const { id, databaseID } = decodeToken(setupProfile.token);
        Amplitude.getInstance(id).track('setupProfileScreenView', {
          email: setupProfile.email,
          country: setupProfile.country,
          databaseID,
          userType: isCreatingWorkspace ? 'newUser' : 'invitedUser',
        });
        setProfile({
          ...profile,
          ...setupProfile,
        });
      }
    } catch (error) {
      Sentry.captureException(error);
      // Gets query parameters from the URL.
      const urlSearchParams = new URLSearchParams(window.location.search);
      const params = Object.fromEntries(urlSearchParams.entries());
      const token = params['token'];
      const { email, country } = decodeToken(token);
      const errorMessage = getSetUpProfileErrorMessageID(
        'DEFAULT',
        location.pathname,
      );
      navigate('/error', {
        replace: true,
        state: {
          profile: {
            email: email,
            country: country,
            token: params.token,
          },
          env,
          errorMessage,
        },
      });
    }
  };

  useEffect(() => {
    if (initDone) return;
    initDone = true;
    const initPage = async () => {
      await fetchEmailAndCountry(isCreatingWorkspace);
      setIsLoaded(true);
    };

    initPage();
  }, [isCreatingWorkspace]);

  const backgroundStyle = {
    backgroundImage: `url(${background})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right bottom',
  };

  const pageContent =
    !profile.sso && activeStep == 0 ? (
      <PasswordField
        profile={profile}
        setProfile={setProfile}
        onClickNext={handleNext}
      />
    ) : (
      <ProfileField
        isCreatingWorkspace={isCreatingWorkspace}
        profile={profile}
        setProfile={setProfile}
      />
    );
  return (
    <div className="App" style={!isMobile ? backgroundStyle : {}}>
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: isMobile ? 'flex-start' : 'flex-end',
          marginTop: '24px',
        }}
      >
        <img src={logo} alt="logo" />
      </div>
      {!isLoaded && <BrickAnimation />}
      {isLoaded && (
        <div
          style={{
            width: isMobile ? '100%' : '378px',
            flex: 1,
            padding: '16px 16px',
          }}
        >
          <p data-testid="set-up-profile" className="headlineMedium">
            {intl.formatMessage({ id: 'set_up_profile' })}
          </p>
          {!profile.sso && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <SetupStepper
                profile={profile}
                steps={steps}
                activeStep={activeStep}
                setActiveStep={handleStep}
              />
            </div>
          )}
          {pageContent}
        </div>
      )}
      <div
        style={{
          flex: 1,
        }}
      ></div>
    </div>
  );
};
